'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { GameDetails } from '../../../casino/types/types';

type UseFavouritesRemovalParams = {
  setGames: React.Dispatch<React.SetStateAction<GameDetails[]>>;
};

type UseFavouritesRemovalReturn = {
  onRemoveGameRef: React.MutableRefObject<(gameName: string, action?: string) => void>;
};

function useFavouritesRemoval({ setGames }: UseFavouritesRemovalParams): UseFavouritesRemovalReturn {
  const [, setRemovedGames] = useState<Array<GameDetails & { originalIndex: number }>>([]);
  const onRemoveGameRef = useRef<(gameName: string, action?: string) => void>(() => {});

  const handleRemoveGame = useCallback(
    (gameName: string, action?: string) => {
      if (action === 'undo') {
        setRemovedGames(currentRemoved => {
          const gameToRestore = currentRemoved.find(game => game.name === gameName);
          if (gameToRestore) {
            setGames(currentGames => {
              const gameExists = currentGames.some(game => game.name === gameName);
              if (!gameExists) {
                const newGames = [...currentGames];
                const { originalIndex, ...gameWithoutIndex } = gameToRestore;
                const gameWithFavourite = { ...gameWithoutIndex, isFavourite: true };
                const insertIndex = Math.min(originalIndex, newGames.length);
                newGames.splice(insertIndex, 0, gameWithFavourite);
                return newGames;
              }
              return currentGames;
            });
            return currentRemoved.filter(game => game.name !== gameName);
          }
          return currentRemoved;
        });
        return;
      }

      setGames(currentGames => {
        const gameIndex = currentGames.findIndex(game => game.name === gameName);
        if (gameIndex !== -1) {
          const gameToRemove = currentGames[gameIndex];
          setRemovedGames(currentRemoved => [{ ...gameToRemove, originalIndex: gameIndex } as GameDetails & { originalIndex: number }, ...currentRemoved]);
        }
        return currentGames.filter(game => game.name !== gameName);
      });
    },
    [setGames]
  );

  useLayoutEffect(() => {
    onRemoveGameRef.current = handleRemoveGame;
  }, [handleRemoveGame]);

  return {
    onRemoveGameRef,
  };
}

export { useFavouritesRemoval };
export type { UseFavouritesRemovalParams, UseFavouritesRemovalReturn };
